import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";

import {
  Button,
  type Column,
  EmptyState,
  Modal,
  PageHead,
  Pill,
  Table,
} from "@/components/common";
import { usePermission } from "@/auth/usePermission";
import { PERM } from "@/auth/permissions";
import { extractErrorMessage } from "@/services/axiosConfig";
import type { WorkspacePlan } from "@/types/workspace";
import { useCreatePlan, usePlans, useUpdatePlan } from "@/hooks/useWorkspaces";
import { formatBRL } from "@/utils/fmt";

export function PlansPage() {
  const canManage = usePermission(PERM.workspacesPlansWrite);
  const { data: plans = [], isLoading } = usePlans();
  const [editing, setEditing] = useState<WorkspacePlan | null>(null);
  const [creating, setCreating] = useState(false);

  if (!canManage) return <Navigate to="/" replace />;

  const columns: Column<WorkspacePlan>[] = [
    { id: "name", header: "Plano", cell: (p) => <strong>{p.name}</strong> },
    { id: "slug", header: "Slug", cell: (p) => <span className="td-small">{p.slug}</span> },
    { id: "mrr", header: "MRR base", align: "right", cell: (p) => formatBRL(p.mrr_base) },
    {
      id: "active",
      header: "Status",
      cell: (p) => <Pill tone={p.active ? "ativa" : "pausada"}>{p.active ? "Ativo" : "Inativo"}</Pill>,
    },
  ];

  return (
    <>
      <PageHead
        title="Planos"
        subtitle="Catálogo de planos comerciais"
        actions={
          <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
            Criar plano
          </Button>
        }
      />
      <Table
        rows={plans}
        columns={columns}
        getKey={(p) => String(p.id)}
        onRowClick={(p) => setEditing(p)}
        empty={<EmptyState icon="credit-card" title={isLoading ? "Carregando…" : "Nenhum plano"} />}
      />
      {creating && <PlanModal onClose={() => setCreating(false)} />}
      {editing && <PlanModal plan={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function PlanModal({ plan, onClose }: { plan?: WorkspacePlan; onClose: () => void }) {
  const createMut = useCreatePlan();
  const updateMut = useUpdatePlan();
  const [name, setName] = useState(plan?.name ?? "");
  const [mrr, setMrr] = useState(plan?.mrr_base ?? "0.00");
  const [active, setActive] = useState(plan?.active ?? true);
  const [error, setError] = useState<string | null>(null);
  const busy = createMut.isPending || updateMut.isPending;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (plan) {
        await updateMut.mutateAsync({ id: plan.id, payload: { name: name.trim(), mrr_base: mrr, active } });
      } else {
        await createMut.mutateAsync({ name: name.trim(), mrr_base: mrr });
      }
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <Modal
      onClose={onClose}
      title={plan ? "Editar plano" : "Criar plano"}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="plan-form" disabled={busy}>
            {busy ? "Salvando…" : "Salvar"}
          </Button>
        </>
      }
    >
      <form id="plan-form" onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {error && <div className="form-error">{error}</div>}
        <label className="form-field">
          <span>Nome</span>
          <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </label>
        <label className="form-field">
          <span>MRR base (R$)</span>
          <input className="form-input" value={mrr} onChange={(e) => setMrr(e.target.value)} required />
        </label>
        {plan && (
          <label className="form-field" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>Ativo</span>
          </label>
        )}
      </form>
    </Modal>
  );
}
